import type { PickEvent } from "eutopia.ts";
import { container, createLayer, createLayerType, line } from "eutopia.ts";
import { $, resolve } from "signlets";

import { turnDistance } from "../configuration";
import { elevation } from "../elevation";
import { groundBearing, groundDistance, move, normalizeAngle } from "../math";
import { dropMission, missionPaths } from "../mission";
import type { Drop, Mode, Position } from "../model";
import { positionEmpty } from "../position";
import { green, red, scaleOpacity, white } from "./color";
import { marker } from "./marker";
import { mission as mission_ } from "./mission";

const { PI } = Math;

const bendLimit = PI / 4;
const limitBendAngle = (_: number) =>
  Math.max(-bendLimit, Math.min(bendLimit, normalizeAngle(_)));

export type DropProps = {
  drop?: Drop;
  editing?: Mode;
  onChangeDrop: (_: Partial<Drop>) => void;
};

export const drop = createLayerType<DropProps>(
  (context, { drop, editing, onChangeDrop }) => {
    const position = $(() => {
      const { drop: [lon = 0, lat = 0] = [], dropAltitude = 0 } =
        resolve(drop) ?? {};
      return [lon, lat, dropAltitude] satisfies Position;
    });
    const start = $((): Position => resolve(drop)?.start ?? [0, 0, 0]);
    const end = $((): Position => resolve(drop)?.end ?? [0, 0, 0]);
    const bend1 = $(() => resolve(drop)?.bend1 ?? 0);
    const bend2 = $(() => resolve(drop)?.bend2 ?? 0);
    const bidirectional = $(() => resolve(drop)?.bidirectional ?? false);
    const valid = $(
      () =>
        !positionEmpty(start()) &&
        !positionEmpty(end()) &&
        !positionEmpty(resolve(position)),
    );
    const mission = $(() => {
      const _ = resolve(drop);
      return _ ? missionPaths(dropMission(_)) : [];
    });
    const length = () => groundDistance(start(), end());
    const bend1Position = () =>
      move(end(), 0.5 * length() + turnDistance, groundBearing(end(), start()));
    const bend2Position = () =>
      move(
        start(),
        0.5 * length() + turnDistance,
        groundBearing(start(), end()),
      );
    const bend1DragPosition = $(() =>
      move(
        bend1Position(),
        turnDistance,
        groundBearing(end(), start()) + bend1(),
      ),
    );
    const bend2DragPosition = $(() =>
      move(
        bend2Position(),
        turnDistance,
        groundBearing(start(), end()) + bend2(),
      ),
    );

    return createLayer(
      context,
      container({
        layers: [
          line({
            vertices: $(() => {
              const color = scaleOpacity(
                red,
                !positionEmpty(start()) && !positionEmpty(end()) ? 1 : 0,
              );
              return [
                [start(), end()].map(position => ({
                  position,
                  color,
                  width: 1,
                  minWidthPixels: 6,
                })),
              ];
            }),
            pickable: false,
            polygonOffset: -1000,
            outline: [0, 0, 0, 1],
          }),
          mission_({
            mission,
            visible: valid,
          }),
          marker({
            markers: [
              {
                position,
                color: $(() =>
                  scaleOpacity(
                    green,
                    !positionEmpty(resolve(position)) ? 1 : 0,
                  ),
                ),
                pickable: $(() => resolve(editing) !== "drop"),
                onDragFlat: $(() => {
                  if (resolve(editing) === "drop") return undefined;
                  return ({ position }: PickEvent) => {
                    resolve(onChangeDrop)({ drop: position });
                  };
                }),
              },
              {
                position: start,
                color: $(() =>
                  scaleOpacity(red, !positionEmpty(start()) ? 1 : 0),
                ),
                pickable: $(() => resolve(editing) !== "start"),
                onDrag: $(() => {
                  const onChange = resolve(onChangeDrop);
                  if (resolve(editing) === "start") return undefined;
                  return ({ position }: PickEvent) => {
                    onChange({ start: position });
                  };
                }),
                onDragEnd: $(() => {
                  const onChange = resolve(onChangeDrop);
                  if (resolve(editing) === "start") return undefined;
                  return async ({ position }: PickEvent) => {
                    const [lon, lat] = position;
                    const alt = await elevation([lon, lat]);
                    onChange({ start: [lon, lat, alt] });
                  };
                }),
              },
              {
                position: end,
                color: $(() =>
                  scaleOpacity(red, !positionEmpty(end()) ? 1 : 0),
                ),
                pickable: $(() => resolve(editing) !== "end"),
                onDrag: $(() => {
                  const onChange = resolve(onChangeDrop);
                  if (resolve(editing) === "end") return undefined;
                  return ({ position }: PickEvent) => {
                    onChange({ end: position });
                  };
                }),
                onDragEnd: $(() => {
                  const onChange = resolve(onChangeDrop);
                  if (resolve(editing) === "end") return undefined;
                  return async ({ position }: PickEvent) => {
                    const [lon, lat] = position;
                    const alt = await elevation([lon, lat]);
                    onChange({ end: [lon, lat, alt] });
                  };
                }),
              },
              {
                position: bend1DragPosition,
                color: $(() => scaleOpacity(white, valid() ? 1 : 0)),
                text: "∠",
                onDragFlat: $(() => {
                  const onChange = resolve(onChangeDrop);
                  return ({ position }: PickEvent) => {
                    let bend1 = limitBendAngle(
                      groundBearing(bend1Position(), position) -
                        groundBearing(end(), start()),
                    );
                    if (Math.abs(bend1) < 0.1) bend1 = 0;
                    onChange({ bend1 });
                  };
                }),
              },
              {
                position: bend2DragPosition,
                text: "∠",
                color: $(() =>
                  scaleOpacity(white, valid() && bidirectional() ? 1 : 0),
                ),
                onDragFlat: $(() => {
                  const onChange = resolve(onChangeDrop);
                  return ({ position }: PickEvent) => {
                    let bend2 = limitBendAngle(
                      groundBearing(bend2Position(), position) -
                        groundBearing(start(), end()),
                    );
                    if (Math.abs(bend2) < 0.1) bend2 = 0;
                    onChange({ bend2 });
                  };
                }),
              },
            ],
          }),
        ],
      }),
    );
  },
);
